import type {
	JiraSideNav,
	JiraSideNavMessageMetadata,
} from '@post-office/placement-create-pipeline/pipes';
import {
	createClientPipeline,
	createServerPipeline,
} from '@post-office/placement-create-pipeline/create-pipeline';
import { type GenerateValidationFor } from '@atlassian/vindicated';
import type { NewIconProps } from '@atlaskit/icon/types';

import { type MessageContextFields, type MessageTemplateContext } from '../../..';
import { type TransformMetadataResult } from '@post-office/shared-contracts/message-template-metadata';
import { type AnyHydrationResult } from '@post-office/shared-contracts/placement-pipe/pipeline-types';
import { type RequestContext } from '@post-office/shared-contracts/shared-request-middleware-context';
import { type JSX } from 'react/jsx-runtime';

export type RecommendedFeature = 'automations' | 'rovo' | 'plans';
export type RecommendedEdition = 'standard' | 'premium';

export type IconComponent = React.ComponentType<NewIconProps>;

/** One entry in the bullet-feature list for a given recommended feature. */
export type RecommendedFeatureItem = {
	/** Unique key for React list rendering */
	id: string;
	/** ADS icon component to render */
	icon: IconComponent;
	/** Pre-rendered i18n message element */
	message: React.ReactElement;
};

/** Per-recommendedFeature copy shape stored in content.tsx */
export type RecommendedFeatureContent = {
	/** Pre-rendered headline element. Accepts a `headlineCount` value via render-prop fn. */
	headline: (headlineCount: number) => React.ReactElement;
	/** Pre-rendered body element. */
	body: React.ReactElement;
	features: ReadonlyArray<RecommendedFeatureItem>;
};

export type HydrationResult = {
	recommendedFeature: RecommendedFeature;
	recommendedFeatureHref: string;
	edition: RecommendedEdition;
	trialLength: number;
	placeOrderLiteHref: string;
	entitlementId: string;
	/** Single count interpolated into the headline (rules / tasks / projects). */
	headlineCount: number;
	/** True when the TAP trait is absent, meaning the modal should auto-open. */
	shouldForceOpenModal: boolean;
};

export type VHydrationResult = GenerateValidationFor<HydrationResult>;

type Pipe = JiraSideNav<{
	messageContext: MessageTemplateContext;
	hydrationResult: HydrationResult;
}>;

const clientPipeline = createClientPipeline<Pipe>();
export const createRender: <
	U extends (
		renderProps: Omit<HydrationResult, 'postOffice'> & {
			onDismissed: () => void;
		},
	) => JSX.Element | null,
>(
	render: U,
) => (
	renderProps: Omit<HydrationResult, 'postOffice'> & {
		onDismissed: () => void;
	},
) => JSX.Element | null = clientPipeline.createRender;
export const createValidate: (
	validate: (apiResponse: unknown) => HydrationResult,
) => (apiResponse: unknown) => HydrationResult = clientPipeline.createValidate;
const serverPipeline = createServerPipeline<Pipe>();
export const createHydrate: (
	hydrate: (
		requestContext: RequestContext,
		placementContext: Record<string, never>,
		messageContext: MessageContextFields & {
			messageInstanceId: string;
			eventTime: string;
		},
	) => Promise<HydrationResult>,
) => (
	requestContext: RequestContext,
	placementContext: Record<string, never>,
	messageContext: MessageContextFields & {
		messageInstanceId: string;
		eventTime: string;
	},
) => Promise<AnyHydrationResult> = serverPipeline.createHydrate;
export const createHydrateExample: (
	hydrateExample: (
		requestContext: Partial<RequestContext>,
		placementContext: Record<string, never>,
		messageContext: MessageContextFields,
	) => HydrationResult,
	config: {
		requestContext?: Partial<RequestContext>;
		additionalRenderProps: Omit<
			Omit<HydrationResult, 'postOffice'> & {
				onDismissed: () => void;
			},
			keyof HydrationResult
		>;
	},
) => (
	requestContext: Partial<RequestContext>,
	placementContext: Record<string, never>,
	messageContext: MessageContextFields & {
		messageInstanceId: string;
		eventTime: string;
	},
) => Omit<HydrationResult, 'postOffice'> & {
	onDismissed: () => void;
} = serverPipeline.createHydrateExample;
export const createTransformMetadata: (
	transformMetadata: (params: {
		messageContext: MessageContextFields;
	}) => JiraSideNavMessageMetadata,
) => (params: { messageContext: MessageContextFields }) => TransformMetadataResult =
	serverPipeline.createTransformMetadata;
